const MAX_IMAGE_METADATA = 128;
const OBSERVER_ROOT_MARGIN_PX = 250;

const rewriteFitInWidth = (url, width) => {
    if (typeof url !== 'string' || !Number.isFinite(width) || width <= 0) return '';
    const dimensionsPattern = /(\/fit-in\/)\d+x\d+(?=\/|[?#]|$)/i;
    if (!dimensionsPattern.test(url)) return '';
    return url.replace(dimensionsPattern, (_, prefix) => `${prefix}${Math.ceil(width)}x0`);
};

const getImageCacheKey = (url) => {
    if (typeof url !== 'string') return '';
    return url.replace(/(\/fit-in\/)\d+x\d+(?=\/|[?#]|$)/i, (_, prefix) => `${prefix}0x0`);
};

const calculateRequiredWidth = ({width, height, dpr, fit, aspectRatio}) => {
    const cssWidth = Number(width);
    const cssHeight = Number(height);
    const pixelRatio = Number(dpr);
    if (!Number.isFinite(cssWidth) || cssWidth <= 0 || !Number.isFinite(pixelRatio) || pixelRatio <= 0) return 0;

    let displayWidth = cssWidth;
    if (Number.isFinite(aspectRatio) && aspectRatio > 0 && Number.isFinite(cssHeight) && cssHeight > 0) {
        if (fit === 'cover') {
            displayWidth = Math.max(cssWidth, cssHeight * aspectRatio);
        } else if (fit === 'contain') {
            displayWidth = Math.min(cssWidth, cssHeight * aspectRatio);
        }
    }

    return Math.ceil(displayWidth * pixelRatio);
};

const getDataAttribute = (element, name) => {
    if (element && element.dataset && element.dataset[name] !== undefined) return element.dataset[name];
    return element && element.getAttribute ? element.getAttribute(`data-${name.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)}`) : null;
};

const setDataAttribute = (element, name, value) => {
    if (element && element.setAttribute) {
        element.setAttribute(`data-${name.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)}`, value);
    }
};

const addClass = (element, className) => {
    if (element && element.classList) element.classList.add(className);
};

const removeClass = (element, className) => {
    if (element && element.classList) element.classList.remove(className);
};

const getUrl = (element) => {
    if (element && element.localName === 'img') return getDataAttribute(element, 'src');
    return getDataAttribute(element, 'backgroundImage');
};

const getLayout = (element, windowObject, getComputedStyle) => {
    const rect = element.getBoundingClientRect ? element.getBoundingClientRect() : {};
    const width = Number(rect.width || element.clientWidth || 0);
    const height = Number(rect.height || element.clientHeight || 0);
    const isImage = element.localName === 'img';
    const style = getComputedStyle ? getComputedStyle(element) : {};
    const explicitMode = getDataAttribute(element, 'responsiveImage');

    if (explicitMode === 'intrinsic') return {width, height, fit: 'intrinsic', intrinsic: true};

    if (isImage) {
        const objectFit = (style.objectFit || 'fill').toLowerCase();
        return {width, height, fit: objectFit, intrinsic: false};
    }

    const backgroundSize = (style.backgroundSize || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (/^auto(?: auto)?$/.test(backgroundSize)) {
        return {width, height, fit: 'intrinsic', intrinsic: true};
    }
    if (backgroundSize === 'cover' || backgroundSize === 'contain') {
        return {width, height, fit: backgroundSize, intrinsic: false};
    }

    const percentage = backgroundSize.match(/^(\d+(?:\.\d+)?)%\s+auto$/);
    if (percentage) {
        return {width: width * Number(percentage[1]) / 100, height, fit: 'fill', intrinsic: false};
    }

    return {width, height, fit: 'fill', intrinsic: false};
};

const isVisibleInLazyRange = (element, windowObject) => {
    if (!element || !element.getBoundingClientRect) return false;
    const rect = element.getBoundingClientRect();
    const viewportWidth = windowObject.innerWidth || (windowObject.document && windowObject.document.documentElement.clientWidth) || 0;
    const viewportHeight = windowObject.innerHeight || (windowObject.document && windowObject.document.documentElement.clientHeight) || 0;
    const margin = OBSERVER_ROOT_MARGIN_PX;
    return rect.width > 0 && rect.height > 0
        && rect.bottom >= -margin && rect.right >= -margin
        && rect.top <= viewportHeight + margin && rect.left <= viewportWidth + margin;
};

const createResponsiveBlockImages = (options) => {
    const config = options || {};
    const windowObject = config.windowObject || (typeof window !== 'undefined' ? window : null);
    const documentObject = config.documentObject || (typeof document !== 'undefined' ? document : null);
    const lozad = config.lozad;
    if (!windowObject || !documentObject || !lozad) return {observe: () => {}};

    const getComputedStyle = config.getComputedStyle || (windowObject.getComputedStyle && windowObject.getComputedStyle.bind(windowObject));
    const createImage = config.createImage || (() => new windowObject.Image());
    const getPlaceholderImage = config.getPlaceholderImage || (() => '');
    const applyImageFallback = config.applyImageFallback || ((image, placeholder) => {
        if (image && placeholder) image.setAttribute('src', placeholder);
    });
    const imageStates = new WeakMap();
    const imageMetadata = new Map();
    const registeredElements = new Set();
    let resizeObserver = null;
    let previousDpr = Number(windowObject.devicePixelRatio) || 1;
    let dprMediaQuery = null;

    const rememberMetadata = (key, nextMetadata) => {
        const previous = imageMetadata.get(key) || {};
        const metadata = Object.assign({}, previous, nextMetadata);
        if (previous.sourceCap && nextMetadata.sourceCap) {
            metadata.sourceCap = Math.min(previous.sourceCap, nextMetadata.sourceCap);
        }
        imageMetadata.delete(key);
        imageMetadata.set(key, metadata);
        while (imageMetadata.size > MAX_IMAGE_METADATA) {
            imageMetadata.delete(imageMetadata.keys().next().value);
        }
        return metadata;
    };

    const getState = (element) => {
        let state = imageStates.get(element);
        const canonicalUrl = getUrl(element);
        const cacheKey = getImageCacheKey(canonicalUrl);
        if (state) {
            if (cacheKey !== state.cacheKey) {
                state.requestId += 1;
                state.canonicalUrl = canonicalUrl;
                state.cacheKey = cacheKey;
                const metadata = imageMetadata.get(cacheKey) || {};
                state.aspectRatio = metadata.aspectRatio || 0;
                state.sourceCap = metadata.sourceCap || 0;
                state.loadedWidth = 0;
                state.requestedWidth = 0;
                state.inFlightWidth = 0;
                state.loaded = false;
                state.failed = false;
                state.failedWidth = 0;
                state.waitingForSize = false;
                if (element.removeAttribute) element.removeAttribute('data-loaded');
                removeClass(element, 'lozad--loaded');
            }
            return state;
        }

        const metadata = imageMetadata.get(cacheKey) || {};
        state = {
            canonicalUrl,
            cacheKey,
            aspectRatio: metadata.aspectRatio || 0,
            sourceCap: metadata.sourceCap || 0,
            loadedWidth: 0,
            requestedWidth: 0,
            inFlightWidth: 0,
            requestId: 0,
            loaded: false,
            failed: false,
            failedWidth: 0,
            waitingForSize: false,
            errorListenerAttached: false
        };
        imageStates.set(element, state);
        return state;
    };

    const getRequiredWidth = (element, state) => {
        const layout = getLayout(element, windowObject, getComputedStyle);
        const validSize = layout.width > 0 && (element.localName === 'img' || layout.height > 0);
        if (!validSize) return {width: 0, layout, validSize: false};
        if (layout.intrinsic) return {width: 0, layout, validSize: true};

        return {
            width: calculateRequiredWidth({
                width: layout.width,
                height: layout.height,
                dpr: Number(windowObject.devicePixelRatio) || 1,
                fit: layout.fit,
                aspectRatio: state.aspectRatio
            }),
            layout,
            validSize: true
        };
    };

    const showFallback = (element, state) => {
        removeClass(element, 'lozad--loading');
        if (state.loaded) return;
        const placeholder = getPlaceholderImage(element);
        if (element.localName === 'img') {
            applyImageFallback(element, placeholder);
            setDataAttribute(element, 'src', state.canonicalUrl);
        } else if (placeholder) {
            element.style.backgroundImage = `url('${placeholder}')`;
        }
        state.failed = true;
        addClass(element, 'lozad--loaded');
        removeClass(element, 'lozad--loading');
    };

    const applyLoadedVariant = (element, state, url, image) => {
        if (element.localName === 'img') {
            element.setAttribute('src', url);
        } else {
            element.style.backgroundImage = `url('${url}')`;
        }

        state.loaded = true;
        state.failed = false;
        state.failedWidth = 0;
        addClass(element, 'lozad--loaded');
        removeClass(element, 'lozad--loading');

        const naturalWidth = Number(image.naturalWidth) || 0;
        const naturalHeight = Number(image.naturalHeight) || 0;
        if (naturalWidth > 0 && naturalHeight > 0) {
            state.aspectRatio = naturalWidth / naturalHeight;
            const metadata = {aspectRatio: state.aspectRatio};
            if (state.requestedWidth > 0 && naturalWidth < state.requestedWidth) {
                state.sourceCap = state.sourceCap ? Math.min(state.sourceCap, naturalWidth) : naturalWidth;
                metadata.sourceCap = state.sourceCap;
            }
            rememberMetadata(state.cacheKey, metadata);
        }
    };

    let responsiveObserver;

    const requestVariant = (element, state, width, intrinsic) => {
        if (state.failed && !state.loaded) return;
        if (!intrinsic && (!Number.isFinite(width) || width <= 0)) return;
        if (intrinsic && (state.loaded || state.inFlightWidth === -1)) return;

        const sourceCap = state.sourceCap || (imageMetadata.get(state.cacheKey) || {}).sourceCap || 0;
        if (!intrinsic && state.failedWidth && width <= state.failedWidth) return;
        if (!intrinsic && sourceCap && width > sourceCap) {
            if (state.loadedWidth >= sourceCap) return;
            width = sourceCap;
        }
        if (!intrinsic && (width <= state.loadedWidth || width <= state.inFlightWidth)) return;

        const url = intrinsic ? state.canonicalUrl : rewriteFitInWidth(state.canonicalUrl, width);
        if (!url) return;

        state.requestId += 1;
        const requestId = state.requestId;
        state.inFlightWidth = intrinsic ? -1 : width;
        state.requestedWidth = intrinsic ? 0 : width;
        state.waitingForSize = false;

        if (element.localName === 'img' && !state.errorListenerAttached && element.addEventListener) {
            state.errorListenerAttached = true;
            element.addEventListener('error', () => {
                if (!state.loaded) showFallback(element, state);
            });
        }

        const image = createImage();
        image.onload = () => {
            if (requestId !== state.requestId) return;
            state.inFlightWidth = 0;
            state.loadedWidth = intrinsic ? 0 : (Number(image.naturalWidth) || width);
            applyLoadedVariant(element, state, url, image);

            if (!intrinsic) {
                const required = getRequiredWidth(element, state);
                const sourceCap = state.sourceCap || (imageMetadata.get(state.cacheKey) || {}).sourceCap || 0;
                const nextWidth = sourceCap ? Math.min(required.width, sourceCap) : required.width;
                if (nextWidth > state.loadedWidth) {
                    requestVariant(element, state, nextWidth, false);
                }
            }
        };
        image.onerror = () => {
            if (requestId !== state.requestId) return;
            state.inFlightWidth = 0;
            state.failedWidth = width;
            showFallback(element, state);
        };
        image.src = url;
    };

    const loadElement = (element) => {
        const state = getState(element);
        const source = getUrl(element);
        if (!state.canonicalUrl && source) state.canonicalUrl = source;
        if (!state.canonicalUrl) {
            showFallback(element, state);
            return;
        }

        const required = getRequiredWidth(element, state);
        if (!required.validSize || (!required.layout.intrinsic && required.width <= 0)) {
            state.waitingForSize = true;
            return;
        }

        addClass(element, 'lozad--loading');
        requestVariant(element, state, required.width, required.layout.intrinsic);
    };

    const handleResolutionChange = (element) => {
        const state = imageStates.get(element);
        if (!state) return;

        const required = getRequiredWidth(element, state);
        if (!required.validSize || (!required.layout.intrinsic && required.width <= 0)) return;

        if (state.waitingForSize) {
            state.waitingForSize = false;
            if (element.removeAttribute) element.removeAttribute('data-loaded');
            responsiveObserver.observe();
            return;
        }

        if (required.layout.intrinsic || state.failed) return;
        const currentWidth = Math.max(state.loadedWidth, state.inFlightWidth > 0 ? state.inFlightWidth : 0);
        const sourceCap = state.sourceCap || (imageMetadata.get(state.cacheKey) || {}).sourceCap || 0;
        const targetWidth = sourceCap ? Math.min(required.width, sourceCap) : required.width;
        if (targetWidth <= currentWidth || (state.failedWidth && targetWidth <= state.failedWidth)) return;

        if (state.loaded && isVisibleInLazyRange(element, windowObject)) {
            requestVariant(element, state, required.width, false);
            return;
        }

        if (element.removeAttribute) element.removeAttribute('data-loaded');
        responsiveObserver.observe();
    };

    const registerElements = () => {
        registeredElements.forEach((element) => {
            if (element.isConnected === false) {
                if (resizeObserver) resizeObserver.unobserve(element);
                registeredElements.delete(element);
                imageStates.delete(element);
            }
        });
        const elements = documentObject.querySelectorAll('[data-responsive-image]');
        Array.prototype.forEach.call(elements, (element) => {
            if (registeredElements.has(element)) {
                getState(element);
                return;
            }
            registeredElements.add(element);
            if (resizeObserver) resizeObserver.observe(element);
        });
    };

    responsiveObserver = lozad('[data-responsive-image]', {
        rootMargin: `${OBSERVER_ROOT_MARGIN_PX}px 0px`,
        load: loadElement,
        loaded: () => {}
    });

    if (windowObject.ResizeObserver) {
        resizeObserver = new windowObject.ResizeObserver((entries) => {
            entries.forEach(entry => handleResolutionChange(entry.target));
        });
    }

    const handleViewportResize = () => {
        const currentDpr = Number(windowObject.devicePixelRatio) || 1;
        const dprChanged = currentDpr !== previousDpr;
        previousDpr = currentDpr;
        if (dprChanged) bindDprMediaQuery();
        registeredElements.forEach(handleResolutionChange);
    };

    const bindDprMediaQuery = () => {
        if (!windowObject.matchMedia) return;
        const mediaQuery = windowObject.matchMedia(`(resolution: ${Number(windowObject.devicePixelRatio) || 1}dppx)`);
        if (dprMediaQuery && dprMediaQuery.removeEventListener) {
            dprMediaQuery.removeEventListener('change', handleViewportResize);
        } else if (dprMediaQuery && dprMediaQuery.removeListener) {
            dprMediaQuery.removeListener(handleViewportResize);
        }
        dprMediaQuery = mediaQuery;
        if (dprMediaQuery.addEventListener) {
            dprMediaQuery.addEventListener('change', handleViewportResize);
        } else if (dprMediaQuery.addListener) {
            dprMediaQuery.addListener(handleViewportResize);
        }
    };

    if (windowObject.addEventListener) windowObject.addEventListener('resize', handleViewportResize);
    bindDprMediaQuery();

    return {
        observe: () => {
            registerElements();
            const elements = documentObject.querySelectorAll('[data-responsive-image]');
            Array.prototype.forEach.call(elements, (element) => {
                const mode = getDataAttribute(element, 'responsiveImage');
                const eager = getDataAttribute(element, 'responsiveEager') === 'true';
                if ((mode === 'intrinsic' || eager) && element.getAttribute('data-loaded') !== 'true') {
                    responsiveObserver.triggerLoad(element);
                }
            });
            responsiveObserver.observe();
        },
        triggerLoad: (element) => responsiveObserver.triggerLoad(element),
        metadata: imageMetadata
    };
};

module.exports = {
    createResponsiveBlockImages,
    calculateRequiredWidth,
    rewriteFitInWidth,
    getImageCacheKey,
    getLayout
};
